/**
 * useServersManagement Hook - Gaming Servers Enterprise Management
 * Hook enterprise per gestione completa server gaming con RCON
 */

import { useState, useEffect, useCallback } from "react";
import { API_ENDPOINTS, apiFetchJson } from "@/lib/api";

export interface ServerPlayer {
    name: string;
    id?: string;
    ping?: number;
    score?: number;
    time_connected?: string;
}

export interface ServerMetrics {
    cpu_percent: number | null;
    memory_mb: number | null;
    memory_percent: number | null;
    network_in: number | null;
    network_out: number | null;
    uptime?: string | null;
}

export interface GameServer {
    id: string;
    name: string;
    game: string;
    status: "online" | "offline" | "starting" | "stopping" | "error";
    players: {
        current: number;
        max: number;
        known?: boolean;
        source?: string;
        list?: ServerPlayer[];
    };
    ip?: string;
    port?: number;
    version?: string;
    map?: string;
    metrics?: ServerMetrics;
    last_seen?: string;
    rcon_enabled?: boolean;
}

export interface ServerAction {
    id: string;
    type: "start" | "stop" | "restart" | "save" | "rcon_command";
    status: "pending" | "running" | "completed" | "failed";
    message?: string;
    timestamp: string;
}

export interface RCONCommand {
    command: string;
    password?: string;
}

export interface PlayerAction {
    player_name: string;
    reason?: string;
}

export function useServersManagement() {
    const [servers, setServers] = useState<GameServer[]>([]);
    const [selectedServer, setSelectedServer] = useState<GameServer | null>(
        null,
    );
    const [serverPlayers, setServerPlayers] = useState<ServerPlayer[]>([]);
    const [serverMetrics, setServerMetrics] = useState<ServerMetrics | null>(
        null,
    );
    const [actions, setActions] = useState<ServerAction[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchServers = useCallback(async () => {
        try {
            setError(null);
            const data = await apiFetchJson<any>(API_ENDPOINTS.SERVERS);
            const serversData = Array.isArray(data) ? data : data?.servers || [];
            setServers(Array.isArray(serversData) ? serversData : []);
        } catch (err) {
            console.error("Error fetching servers:", err);
            setError(err instanceof Error ? err.message : "Unknown error");
            setServers([]);
        }
    }, []);

    const fetchServerPlayers = useCallback(async (serverId: string) => {
        try {
            if (serverId === "zomboid") {
                const data = await apiFetchJson<any>(API_ENDPOINTS.ZOMBOID_PLAYERS);
                setServerPlayers(Array.isArray(data?.players) ? data.players : []);
                return;
            }

            const data = await apiFetchJson<any>(
                API_ENDPOINTS.RCON_PLAYERS(serverId),
            );
            setServerPlayers(Array.isArray(data?.players) ? data.players : []);
        } catch (err) {
            console.error("Error fetching server players:", err);
            setServerPlayers([]);
        }
    }, []);

    const fetchServerMetrics = useCallback(async (serverId: string) => {
        try {
            const data = await apiFetchJson<any>(API_ENDPOINTS.SERVERS_PERFORMANCE);
            const perf = data?.servers_performance?.[serverId];
            if (!perf || perf.status === "error") {
                setServerMetrics(null);
                return;
            }

            const numOrNull = (v: unknown): number | null => {
                if (typeof v !== "number") return null;
                return Number.isFinite(v) ? v : null;
            };
            const strOrNull = (v: unknown): string | null => {
                if (typeof v !== "string") return null;
                const s = v.trim();
                return s ? s : null;
            };

            setServerMetrics({
                cpu_percent: numOrNull(perf.cpu),
                memory_mb: numOrNull(perf.memory_mb),
                memory_percent: numOrNull(perf.memory),
                network_in: numOrNull(perf.network_rx_mb),
                network_out: numOrNull(perf.network_tx_mb),
                uptime: strOrNull(perf.uptime),
            });
        } catch (err) {
            console.error("Error fetching server metrics:", err);
            setServerMetrics(null);
            setError(err instanceof Error ? err.message : "Unknown error");
        }
    }, []);

    const executeRCONCommand = useCallback(
        async (serverId: string, command: RCONCommand) => {
            try {
                const result =
                    serverId === "zomboid"
                        ? await apiFetchJson<any>(API_ENDPOINTS.ZOMBOID_RCON, {
                              method: "POST",
                              query: { command: command.command },
                          })
                        : await apiFetchJson<any>(
                              API_ENDPOINTS.RCON_COMMAND(serverId),
                              {
                                  method: "POST",
                                  json: command,
                              },
                          );

                // Add action to history
                const action: ServerAction = {
                    id: Date.now().toString(),
                    type: "rcon_command",
                    status: "completed",
                    message: `Command executed: ${command.command}`,
                    timestamp: new Date().toISOString(),
                };

                setActions((prev) => [action, ...prev.slice(0, 9)]); // Keep last 10 actions

                return result;
            } catch (err) {
                const msg = err instanceof Error ? err.message : "Unknown error";
                const action: ServerAction = {
                    id: Date.now().toString(),
                    type: "rcon_command",
                    status: "failed",
                    message: msg,
                    timestamp: new Date().toISOString(),
                };

                setActions((prev) => [action, ...prev.slice(0, 9)]);

                return {
                    success: false,
                    error: msg,
                };
            }
        },
        [],
    );

    const kickPlayer = useCallback(
        async (serverId: string, playerAction: PlayerAction) => {
            try {
                const result =
                    serverId === "zomboid"
                        ? await apiFetchJson<any>(
                              API_ENDPOINTS.ZOMBOID_KICK(playerAction.player_name),
                              { method: "POST" },
                          )
                        : await apiFetchJson<any>(API_ENDPOINTS.RCON_KICK(serverId), {
                              method: "POST",
                              json: playerAction,
                          });

                const action: ServerAction = {
                    id: Date.now().toString(),
                    type: "rcon_command",
                    status: "completed",
                    message: `Player kicked: ${playerAction.player_name}`,
                    timestamp: new Date().toISOString(),
                };

                setActions((prev) => [action, ...prev.slice(0, 9)]);

                // Refresh players list
                await fetchServerPlayers(serverId);

                return result;
            } catch (err) {
                const msg = err instanceof Error ? err.message : "Unknown error";
                return {
                    success: false,
                    error: msg,
                };
            }
        },
        [fetchServerPlayers],
    );

    const banPlayer = useCallback(
        async (serverId: string, playerAction: PlayerAction) => {
            try {
                const result =
                    serverId === "zomboid"
                        ? await apiFetchJson<any>(
                              API_ENDPOINTS.ZOMBOID_BAN(playerAction.player_name),
                              { method: "POST" },
                          )
                        : await apiFetchJson<any>(API_ENDPOINTS.RCON_BAN(serverId), {
                              method: "POST",
                              json: playerAction,
                          });

                const action: ServerAction = {
                    id: Date.now().toString(),
                    type: "rcon_command",
                    status: "completed",
                    message: `Player banned: ${playerAction.player_name}`,
                    timestamp: new Date().toISOString(),
                };

                setActions((prev) => [action, ...prev.slice(0, 9)]);

                // Refresh players list
                await fetchServerPlayers(serverId);

                return result;
            } catch (err) {
                const msg = err instanceof Error ? err.message : "Unknown error";
                return {
                    success: false,
                    error: msg,
                };
            }
        },
        [fetchServerPlayers],
    );

    const broadcastMessage = useCallback(
        async (serverId: string, message: string) => {
            try {
                const result =
                    serverId === "zomboid"
                        ? await apiFetchJson<any>(API_ENDPOINTS.ZOMBOID_BROADCAST, {
                              method: "POST",
                              query: { message },
                          })
                        : await apiFetchJson<any>(
                              API_ENDPOINTS.RCON_BROADCAST(serverId),
                              {
                                  method: "POST",
                                  json: { message },
                              },
                          );

                const action: ServerAction = {
                    id: Date.now().toString(),
                    type: "rcon_command",
                    status: "completed",
                    message: `Message broadcasted: ${message}`,
                    timestamp: new Date().toISOString(),
                };

                setActions((prev) => [action, ...prev.slice(0, 9)]);

                return result;
            } catch (err) {
                const msg = err instanceof Error ? err.message : "Unknown error";
                return {
                    success: false,
                    error: msg,
                };
            }
        },
        [],
    );

    const saveServer = useCallback(async (serverId: string) => {
        try {
            const result =
                serverId === "zomboid"
                    ? await apiFetchJson<any>(API_ENDPOINTS.ZOMBOID_SAVE, {
                          method: "POST",
                      })
                    : await apiFetchJson<any>(API_ENDPOINTS.RCON_SAVE(serverId), {
                          method: "POST",
                      });

            const action: ServerAction = {
                id: Date.now().toString(),
                type: "save",
                status: "completed",
                message: "Server saved successfully",
                timestamp: new Date().toISOString(),
            };

            setActions((prev) => [action, ...prev.slice(0, 9)]);

            return result;
        } catch (err) {
            const msg = err instanceof Error ? err.message : "Unknown error";
            return {
                success: false,
                error: msg,
            };
        }
    }, []);

    const selectServer = useCallback(
        (server: GameServer) => {
            setSelectedServer(server);
            fetchServerPlayers(server.id);
            fetchServerMetrics(server.id);
        },
        [fetchServerPlayers, fetchServerMetrics],
    );

    const refresh = useCallback(async () => {
        setIsLoading(true);
        await fetchServers();
        if (selectedServer) {
            await Promise.all([
                fetchServerPlayers(selectedServer.id),
                fetchServerMetrics(selectedServer.id),
            ]);
        }
        setIsLoading(false);
    }, [fetchServers, selectedServer, fetchServerPlayers, fetchServerMetrics]);

    useEffect(() => {
        refresh();

        // Auto-refresh every 30 seconds
        const interval = setInterval(refresh, 30000);
        return () => clearInterval(interval);
    }, [refresh]);

    return {
        servers,
        selectedServer,
        serverPlayers,
        serverMetrics,
        actions,
        isLoading,
        error,
        selectServer,
        refresh,
        executeRCONCommand,
        kickPlayer,
        banPlayer,
        broadcastMessage,
        saveServer,
    };
}
